import type z from 'zod/v4'
import { relations } from 'drizzle-orm'
import { index, integer, pgTable, text, timestamp, uniqueIndex, uuid } from 'drizzle-orm/pg-core'
import { createInsertSchema, createSelectSchema, createUpdateSchema } from 'drizzle-zod'

import { numericAsHighPrecision } from '../../custom-type'
import { Book } from '../content'
import { Royalty } from './royalty'

export const RoyaltyToBooks = pgTable(
  'royalty_to_books',
  {
    id: uuid().primaryKey().defaultRandom(),
    royaltyId: uuid()
      .notNull()
      .references(() => Royalty.id, { onDelete: 'restrict' }),
    bookId: uuid()
      .notNull()
      .references(() => Book.id, { onDelete: 'restrict' }),
    uniqueSession: integer().notNull().default(0),
    sessionRate: numericAsHighPrecision().notNull(),
    earnedAmount: numericAsHighPrecision().notNull(),
    currency: text().notNull().default('IDR'),
    calculatedAt: timestamp({ mode: 'date', withTimezone: true }),
    createdAt: timestamp({ mode: 'date', withTimezone: true }).notNull().defaultNow(),
  },
  (t) => [
    index('royalty_to_books_royalty_idx').on(t.royaltyId),
    index('royalty_to_books_book_idx').on(t.bookId),
    uniqueIndex('royalty_to_books_royalty_book_uidx').on(t.royaltyId, t.bookId),
  ],
)

export const royaltyToBooksRelations = relations(RoyaltyToBooks, ({ one }) => ({
  royalty: one(Royalty, {
    fields: [RoyaltyToBooks.royaltyId],
    references: [Royalty.id],
  }),
  book: one(Book, {
    fields: [RoyaltyToBooks.bookId],
    references: [Book.id],
  }),
}))

export const InsertRoyaltyToBooksSchema = createInsertSchema(RoyaltyToBooks).omit({
  id: true,
  createdAt: true,
})

export const UpdateRoyaltyToBooksSchema = createUpdateSchema(RoyaltyToBooks).partial().omit({
  id: true,
  royaltyId: true,
  bookId: true,
  createdAt: true,
})

export const SelectRoyaltyToBooksSchema = createSelectSchema(RoyaltyToBooks)

export type InsertRoyaltyToBooks = typeof RoyaltyToBooks.$inferInsert
export type UpdateRoyaltyToBooks = z.infer<typeof UpdateRoyaltyToBooksSchema>
export type SelectRoyaltyToBooks = typeof RoyaltyToBooks.$inferSelect
